import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { SWRResponse } from 'swr';

const PAGE_SIZE = 6;

export const createInitialScrollState = <T, F = Record<string, unknown>>(
  overrides?: Partial<ScrollState<T, F>>,
): ScrollState<T, F> => ({
  items: [],
  total: 0,
  page: 1,
  pageSize: PAGE_SIZE,
  shouldFetch: true,
  filter: {} as F,
  ...overrides,
});

export type ScrollState<T, F = Record<string, unknown>> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  shouldFetch: boolean;
  filter: F;
};

export type InfiniteScrollFetcher<T = unknown, F = Record<string, unknown>> = (
  shouldFetch: boolean,
  pagination: InfiniteScrollPagination,
  filter: F,
  controller: AbortController,
) => SWRResponse<{ items: T[]; total: number }>;

type ScrollAction<T, F> =
  | { type: 'SET_DATA'; payload: { items: T[]; total: number } }
  | { type: 'INCREMENT_PAGE' }
  | { type: 'RESET' }
  | { type: 'SET_SHOULD_FETCH'; payload: boolean }
  | { type: 'SET_FILTER'; payload: F };

export const scrollReducer = <T extends { id: string }, F>(
  state: ScrollState<T, F>,
  action: ScrollAction<T, F>,
): ScrollState<T, F> => {
  switch (action.type) {
    case 'SET_DATA': {
      const existingIds = new Set(state.items.map((v) => v.id));
      const newItems = new Set(action.payload?.items?.filter((item) => !existingIds.has(item.id)));

      return {
        ...state,
        items: [...state.items, ...newItems],
        total: action.payload.total,
        shouldFetch: false,
      };
    }
    case 'INCREMENT_PAGE':
      return { ...state, page: state.page + 1 };

    case 'RESET':
      return createInitialScrollState();

    case 'SET_SHOULD_FETCH':
      return { ...state, shouldFetch: action.payload };

    case 'SET_FILTER':
      return { ...state, filter: action.payload };

    default:
      return state;
  }
};

export type InfiniteScrollPagination = {
  page: number;
  pageSize: number;
};

export const useInfiniteScroll = <T, F = Record<string, unknown>>({
  fetchHook,
  reducer = scrollReducer as React.Reducer<ScrollState<T, F>, ScrollAction<T, F>>,
  initialState,
  dependencies = [],
  onError,
  preventFetch,
}: {
  fetchHook: InfiniteScrollFetcher<T, F>;
  initialState: ScrollState<T, F>;
  reducer?: React.Reducer<ScrollState<T, F>, ScrollAction<T, F>>;
  dependencies?: unknown[];
  onError?: (error: any) => void;
  // TODO: Remove this and fix the re-render loop bug
  preventFetch?: boolean;
}) => {
  const [controller, setController] = useState(new AbortController());
  const [state, dispatch] = useReducer(reducer, initialState);
  const { items, total, page, pageSize, shouldFetch } = state;
  const pagination = useMemo(() => ({ page, pageSize }), [page, pageSize]);
  const {
    data: fetchedDataPage,
    error,
    isValidating,
    mutate,
  } = fetchHook(!preventFetch && shouldFetch, pagination, state.filter, controller);

  const observer = useRef<IntersectionObserver | null>(null);
  const hasMore = useMemo(() => total !== 0 && items.length < total, [items.length, total]);
  const isFetchingInitial = useMemo(
    () => !fetchedDataPage && shouldFetch && page === 1,
    [fetchedDataPage, page, shouldFetch],
  );
  const isFetchingNext = useMemo(() => shouldFetch && page > 1, [page, shouldFetch]);

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingInitial || isFetchingNext || !node) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          dispatch({ type: 'INCREMENT_PAGE' });
          dispatch({ type: 'SET_SHOULD_FETCH', payload: true });
        }
      });

      observer.current.observe(node);
    },
    [hasMore, isFetchingInitial, isFetchingNext],
  );

  useEffect(() => {
    if (!fetchedDataPage || isValidating || preventFetch) return;

    dispatch({
      type: 'SET_DATA',
      payload: {
        items: fetchedDataPage.items,
        total: fetchedDataPage.total,
      },
    });
  }, [fetchedDataPage, isValidating, preventFetch, ...dependencies]);

  useEffect(() => {
    if (error && error.code !== 'ERR_CANCELED') {
      onError?.(error);
    }
  }, [error, onError]);

  const resetAndRefetch = useCallback(() => {
    if (preventFetch) return;

    dispatch({ type: 'RESET' });
    mutate();
  }, [mutate, preventFetch]);

  const setFilter = useCallback((filter: F) => {
    setController((p) => {
      p.abort();
      return new AbortController();
    });

    dispatch({ type: 'RESET' });
    dispatch({ type: 'SET_FILTER', payload: filter });
  }, []);

  return {
    items,
    total,
    lastElementRef,
    isFetchingNext,
    isFetchingInitial,
    error,
    resetAndRefetch,
    setFilter,
    filter: state.filter,
  };
};
